import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { aside, landing, within, type RowBox } from "../lib/reorder";

/** A finger rests this long on a row before the row is taken; a quicker move scrolls the list. */
export const HOLD_MS = 350;
/** A mouse takes a row by moving it this far; less is a click. */
const MOUSE_START_PX = 5;
/** A finger that moves further than this before the row is taken is scrolling. */
const TOUCH_SLOP_PX = 10;
/** This near the top or the bottom of a list that scrolls, the list scrolls along. */
const EDGE_PX = 48;
const SCROLL_PX_PER_FRAME = 14;
/**
 * What a browser still sends after the release of a pointer that held a row (a click, the menu of a long
 * press) comes within this time; a new press ends the wait at once.
 */
const AFTER_RELEASE_MS = 400;

/**
 * How much smaller or larger the screen draws an element than its own pixels say: 0.9 in a till at size
 * "S" (ADR 0021). A browser says where something stands and where the pointer is in pixels of the screen;
 * a style, and how far a list has scrolled, are in the element's own pixels.
 */
function drawnScale(element: Element): number {
  // a browser that cannot say is taken to draw at full size
  const zoom: unknown = element.currentCSSZoom;
  return typeof zoom === "number" && zoom > 0 ? zoom : 1;
}

/** The row that is being moved. */
export interface TakenRow {
  id: string;
  from: number;
  /** The place it would land at now. */
  to: number;
  /** How far it is from where it was taken, in pixels. */
  offset: number;
  /** Its height and the gap under it: how far the rows in between move aside. */
  space: number;
}

interface Session {
  id: string;
  index: number;
  pointerId: number;
  touch: boolean;
  startX: number;
  startY: number;
  lastY: number;
  /** The farthest the pointer went from where it came down, in pixels of the screen. */
  travel: number;
  /** How much smaller the screen draws the list than its own pixels say. */
  scale: number;
  startScroll: number;
  /** As they stood when the row was taken, in the list's own pixels. */
  rows: RowBox[];
  space: number;
  taken: boolean;
  timer: ReturnType<typeof setTimeout> | undefined;
  frame: number | undefined;
}

/**
 * How a move ends: the row is put where the pointer let it go; or it goes back while the pointer is still
 * down (Escape, another window, other rows); or the pointer is gone (the browser took it to scroll).
 */
type Ending = "put" | "back" | "lost";

interface Options {
  /** Nothing can be taken (the list is being saved, or it is not in an order of its own to change). */
  disabled?: boolean;
  /** A row was let go at another place. */
  onMove: (id: string, to: number) => void;
}

export interface Reorder {
  taken: TakenRow | null;
  /** For the element that holds the rows (and scrolls, when they do not fit). */
  listRef: (element: HTMLElement | null) => void;
  /** For each row, the element around the grip and what the row shows; it carries `data-row={id}`. */
  rowHandlers: {
    onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
    onClickCapture: (event: ReactMouseEvent<HTMLElement>) => void;
    onContextMenu: (event: ReactMouseEvent<HTMLElement>) => void;
  };
  /**
   * For the grip of a row, which carries `data-grip={id}`: it takes the row at once, and the arrow keys
   * move the row one place.
   */
  gripHandlers: {
    onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => void;
  };
}

/**
 * How far the pointer carried the held row from where it was taken, in the list's own pixels: what the
 * pointer moved on the screen, and what the list scrolled meanwhile.
 */
function carried(session: Session, list: HTMLElement | null): number {
  return (session.lastY - session.startY) / session.scale + (list?.scrollTop ?? 0) - session.startScroll;
}

/** Where a row stands while one is held: the held one under the pointer, the others making room. */
export function rowStyle(taken: TakenRow | null, id: string, index: number): CSSProperties | undefined {
  if (taken === null) return undefined;
  const moved = taken.id === id ? taken.offset : aside(index, taken.from, taken.to, taken.space);
  return { transform: `translateY(${String(moved)}px)` };
}

/**
 * Rows of a list moved by hand (ADR 0020; Furkan, 03/10/2026: "vasthouden en schuiven"). A row is taken
 * by its grip, at once; with the mouse also by the row itself, as soon as it moves; with a finger by
 * resting on the row (a quick move still scrolls the list). The taken row follows the pointer, the others
 * make room, and near the edge a long list scrolls along. Letting go puts it there; Escape puts it back.
 * With the keys: the arrows on a grip move its row one place.
 */
export function useReorder(ids: readonly string[], { disabled = false, onMove }: Options): Reorder {
  const [taken, setTaken] = useState<TakenRow | null>(null);
  const list = useRef<HTMLElement | null>(null);
  const session = useRef<Session | null>(null);
  /**
   * What the browser still sends after the release of a pointer that held a row: a click, which is not a
   * tap on a row when the row was slid, and after a long press its menu, which is never wanted here.
   */
  const after = useRef({ click: false, menu: false });
  const afterUntil = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  /** The pointer of a move that ended while it was still down: its release is the release of that move. */
  const spent = useRef<number | null>(null);
  const focusGrip = useRef<string | null>(null);
  const latest = useRef({ ids, onMove, disabled });
  useLayoutEffect(() => {
    latest.current = { ids, onMove, disabled };
  });

  /** Where the taken row is now, from the pointer and from how far the list scrolled meanwhile. */
  const follow = useCallback(() => {
    const current = session.current;
    if (!current?.taken) return;
    const offset = within(current.rows, current.index, carried(current, list.current));
    setTaken({
      id: current.id,
      from: current.index,
      to: landing(current.rows, current.index, offset),
      offset,
      space: current.space,
    });
  }, []);

  /** A pointer that held a row was released; `slid`: it moved the row, so its click is not a tap. */
  const released = useCallback((slid: boolean) => {
    after.current = { click: slid, menu: true };
    clearTimeout(afterUntil.current);
    afterUntil.current = setTimeout(() => {
      after.current = { click: false, menu: false };
    }, AFTER_RELEASE_MS);
  }, []);

  const end = useCallback(
    (how: Ending) => {
      const current = session.current;
      if (!current) return;
      session.current = null;
      clearTimeout(current.timer);
      if (current.frame !== undefined) cancelAnimationFrame(current.frame);
      if (!current.taken) return;
      setTaken(null);
      if (how === "back") spent.current = current.pointerId;
      if (how !== "put") return;
      const scrolled = (list.current?.scrollTop ?? 0) - current.startScroll;
      const offset = within(current.rows, current.index, carried(current, list.current));
      const to = landing(current.rows, current.index, offset);
      // A row that was held and let go without sliding it was pressed: a slow press on a row, as many
      // give a touch screen, still opens its product.
      const slop = current.touch ? TOUCH_SLOP_PX : MOUSE_START_PX;
      released(to !== current.index || scrolled !== 0 || current.travel > slop);
      if (to !== current.index) latest.current.onMove(current.id, to);
    },
    [released],
  );

  const take = useCallback(() => {
    const current = session.current;
    const holder = list.current;
    if (!current || current.taken || !holder) return;
    const frame = holder.getBoundingClientRect();
    const scale = drawnScale(holder);
    current.scale = scale;
    current.rows = [...holder.querySelectorAll<HTMLElement>("[data-row]")].map((element) => {
      const box = element.getBoundingClientRect();
      return { top: (box.top - frame.top) / scale + holder.scrollTop, height: box.height / scale };
    });
    const own = current.rows[current.index];
    if (own === undefined) {
      end("lost");
      return;
    }
    // the room between two rows, measured at the first two
    const [first, second] = current.rows;
    const gap = first !== undefined && second !== undefined ? Math.max(0, second.top - first.top - first.height) : 0;
    current.space = own.height + gap;
    current.startScroll = holder.scrollTop;
    current.taken = true;
    follow();
    // near the top or the bottom the list scrolls along, and the row keeps up with it
    const scroll = () => {
      const now = session.current;
      if (now !== current || !now.taken) return;
      const box = holder.getBoundingClientRect();
      const edge = EDGE_PX * now.scale;
      const above = box.top + edge - now.lastY;
      const below = now.lastY - (box.bottom - edge);
      const speed = above > 0 ? -Math.min(1, above / edge) : below > 0 ? Math.min(1, below / edge) : 0;
      if (speed !== 0) {
        const before = holder.scrollTop;
        holder.scrollTop = before + speed * SCROLL_PX_PER_FRAME;
        if (holder.scrollTop !== before) follow();
      }
      now.frame = requestAnimationFrame(scroll);
    };
    current.frame = requestAnimationFrame(scroll);
  }, [end, follow]);

  // the pointer, wherever it goes while a row is held
  useEffect(() => {
    // a new press: the click that follows it is a tap again
    const down = () => {
      spent.current = null;
      after.current = { click: false, menu: false };
    };
    const move = (event: PointerEvent) => {
      const current = session.current;
      if (current?.pointerId !== event.pointerId) return;
      current.lastY = event.clientY;
      const distance = Math.hypot(event.clientX - current.startX, event.clientY - current.startY);
      current.travel = Math.max(current.travel, distance);
      if (current.taken) {
        follow();
        return;
      }
      if (current.touch) {
        // a finger that moves before the row is taken scrolls the list
        if (distance > TOUCH_SLOP_PX) end("lost");
      } else if (distance > MOUSE_START_PX) take();
    };
    const up = (event: PointerEvent) => {
      if (session.current?.pointerId === event.pointerId) end("put");
      else if (spent.current === event.pointerId) {
        spent.current = null;
        released(true);
      }
    };
    const cancel = (event: PointerEvent) => {
      if (session.current?.pointerId === event.pointerId) end("lost");
      else if (spent.current === event.pointerId) spent.current = null;
    };
    const key = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || !session.current?.taken) return;
      event.preventDefault();
      event.stopPropagation();
      end("back");
    };
    // a finger that holds a row moves the row, not the page
    const touch = (event: TouchEvent) => {
      if (session.current?.taken === true && event.cancelable) event.preventDefault();
    };
    const leave = () => {
      end("back");
    };
    window.addEventListener("pointerdown", down, true);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel);
    window.addEventListener("keydown", key, true);
    window.addEventListener("touchmove", touch, { passive: false });
    window.addEventListener("blur", leave);
    return () => {
      window.removeEventListener("pointerdown", down, true);
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
      window.removeEventListener("keydown", key, true);
      window.removeEventListener("touchmove", touch);
      window.removeEventListener("blur", leave);
      end("lost");
      clearTimeout(afterUntil.current);
    };
  }, [end, released, follow, take]);

  // The rows changed (another device changed the menu): a row that is held goes back, since its place
  // was measured among the rows as they were. A row moved with the keys keeps the keys: its grip has the
  // focus again at its new place.
  const order = ids.join("\n");
  useLayoutEffect(() => {
    end("back");
    const id = focusGrip.current;
    if (id === null) return;
    focusGrip.current = null;
    const grips = list.current?.querySelectorAll<HTMLElement>("[data-grip]") ?? [];
    for (const grip of grips) if (grip.dataset.grip === id) grip.focus();
  }, [order, end]);

  const listRef = useCallback((element: HTMLElement | null) => {
    list.current = element;
  }, []);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const id = event.currentTarget.dataset.row;
      const index = id === undefined ? -1 : latest.current.ids.indexOf(id);
      if (id === undefined || index < 0 || latest.current.disabled) return;
      if (event.button !== 0 || !event.isPrimary || session.current) return;
      const byGrip = event.target instanceof Element && event.target.closest("[data-grip]") !== null;
      const touch = event.pointerType === "touch";
      session.current = {
        id,
        index,
        pointerId: event.pointerId,
        touch,
        startX: event.clientX,
        startY: event.clientY,
        lastY: event.clientY,
        travel: 0,
        scale: 1,
        startScroll: 0,
        rows: [],
        space: 0,
        taken: false,
        // a finger at rest on the row takes it; the grip takes it at once
        timer: !byGrip && touch ? setTimeout(take, HOLD_MS) : undefined,
        frame: undefined,
      };
      if (byGrip) take();
    },
    [take],
  );

  const onClickCapture = useCallback((event: ReactMouseEvent<HTMLElement>) => {
    if (!after.current.click) return;
    after.current.click = false;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  // a finger at rest is not a right click here
  const onContextMenu = useCallback((event: ReactMouseEvent<HTMLElement>) => {
    if (session.current !== null || after.current.menu) event.preventDefault();
  }, []);

  const onKeyDown = useCallback((event: ReactKeyboardEvent<HTMLElement>) => {
    const step = event.key === "ArrowUp" ? -1 : event.key === "ArrowDown" ? 1 : 0;
    const id = event.currentTarget.dataset.grip;
    if (step === 0 || id === undefined || latest.current.disabled) return;
    event.preventDefault();
    const to = latest.current.ids.indexOf(id) + step;
    if (to < 0 || to >= latest.current.ids.length) return;
    focusGrip.current = id;
    latest.current.onMove(id, to);
  }, []);

  return {
    taken,
    listRef,
    rowHandlers: { onPointerDown, onClickCapture, onContextMenu },
    gripHandlers: { onKeyDown },
  };
}
